/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import apiService from '../../../../../../share/util/api/service';
import { executePolling, REQUEST_STATUS_COMPLETED, REQUEST_STATUS_FAILED, getResponseCheckDefaultAction } from '../../../../../../share/util/api/polling';
import { apiErrorDefaultAction } from "../../../../../../share/util/api/errorAction";
import logWrapper from '../../../../../../share/logWrapper';
import diagnoseResource from '../../../../../../share/util/diagnose';

/** 各フェーズ定義 */
const PHASE = {
  START: "start",
  SUCCESS: "success",
  FAILED: "failed",
  ERROR: "error"
};

/** エラー定義 */
const ERROR_LIST = {
  OBD_SYSTEM_TEST_EXECUTE_ERROR: 'execute_error'
};

export default {
  name: "executeModal",
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      executeModal: false
    };
  },

  props: {
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId,
      obdDataId: state => state.obd.obdDataId,
      systemNo: state => state.obd.systemNo,
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED:
        case PHASE.ERROR:
          return "danger";
        case PHASE.SUCCESS:
          return "success";
        case PHASE.START:
        default:
          return "secondary";
      }
    },

    // 診断系のAPIは複数画面から使用されているため
    // 現在の画面で使用されるAPIが診断/データ再生/個別診断/OBDシステムのうちどの機能なのか判別する
    operationScreen() {
      return diagnoseResource.operationFrom(this.$router.currentRoute.value.name);
    }
  },

  methods: {
    ...mapActions("auth", ["logout", "resetSession"]),

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    async init() {
      this.executeModal = true;
      await this.execute();
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.executeModal = false;
      this.$emit("close-wizard");
    },

    /** オンボードシステムテスト */
    async execute() {
      try {
        this.isLoading = true;
        const response = await executePolling(
          this,
          // 呼び出したい POST API を実装
          async () =>
            await apiService.postCall(apiService.OBD, `/netApps/${this.netAppId}/obd/${this.obdDataId}/systems/${this.$route.params.systemNo}/systemTest`, {}, this.operationScreen),
          // 呼び出したい GET API を実装
          async (postApiResponse) =>
            await apiService.getCall(apiService.OBD, `/netApps/${this.netAppId}/requests/${postApiResponse?.data?.request_id}`, this.operationScreen),
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          undefined,
          // GET API レスポンスチェックのコールバックの処理を実装
          (getApiResponse) => {
            if (getApiResponse?.data?.status === REQUEST_STATUS_FAILED && getApiResponse?.data?.error_code) {
              // failed の場合のハンドリングはここでは行わない（エラーに倒さない）
              logWrapper.log(
                "receive failed response. continue.",
                getApiResponse?.data,
                true
              );
              return REQUEST_STATUS_COMPLETED;
            }
            return getResponseCheckDefaultAction(getApiResponse);
          },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); }
        );

        if (response) {
          switch (response.data.status) {
            case REQUEST_STATUS_COMPLETED:
              // 実行開始表示
              this.phase = PHASE.SUCCESS;
              break;
            case REQUEST_STATUS_FAILED:
              // failed の場合のハンドリングはここで行う（条件に応じてエラーを出し分ける）
              if (response.data.error_code === ERROR_LIST.OBD_SYSTEM_TEST_EXECUTE_ERROR) {
                // 実行条件不成立表示
                this.phase = PHASE.FAILED;
                break;
              } else {
                this.showUnexpectedError();
                break;
              }
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 異常終了：エラー表示
      this.phase = PHASE.ERROR;
    }
  },

  async mounted() {
    this.init();
  }
};
